/**
 * Manufacturer Registration Review Component
 * Displays detailed registration information for admin review
 */

import { useState, useEffect } from 'react';
import { adminService } from '../services/admin.service';
import { Button, Modal } from './ui';

interface ManufacturerRegistrationReviewProps {
  manufacturerId: string;
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

export function ManufacturerRegistrationReview({
  manufacturerId,
  isOpen,
  onClose,
  onApprove,
  onReject
}: ManufacturerRegistrationReviewProps) {
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && manufacturerId) {
      loadRegistrationData();
    }
  }, [isOpen, manufacturerId]);

  const loadRegistrationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getManufacturerRegistrationDetails(manufacturerId);
      setRegistrationData(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load registration details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatValidationStatus = (status: string) => {
    const statusMap: { [key: string]: { label: string; color: string } } = {
      'verified': { label: 'Verified', color: 'text-green-600 bg-green-100' },
      'pending': { label: 'Pending', color: 'text-yellow-600 bg-yellow-100' },
      'failed': { label: 'Failed', color: 'text-red-600 bg-red-100' },
      'manual_review_required': { label: 'Manual Review', color: 'text-orange-600 bg-orange-100' }
    };
    
    const statusInfo = statusMap[status] || { label: status, color: 'text-gray-600 bg-gray-100' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manufacturer Registration Review" size="large">
      <div className="max-h-[80vh] overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading registration details...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {registrationData && (
          <div className="space-y-8">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Name</label>
                  <p className="mt-1 text-sm text-gray-900">{registrationData.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{registrationData.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Registration Status</label>
                  <p className="mt-1 text-sm text-gray-900">{registrationData.registrationStatus}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Submitted At</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(registrationData.registrationSubmittedAt)}</p>
                </div>
              </div>
            </div>

            {/* Company Details */}
            {registrationData.companyDetails && (
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Registered Name</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.companyDetails.registeredName || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Entity Type</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.companyDetails.entityType || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Incorporation</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(registrationData.companyDetails.dateOfIncorporation)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country of Incorporation</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.companyDetails.countryOfIncorporation || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Facility Type</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.companyDetails.facilityType || 'Not provided'}</p>
                  </div>
                </div>

                {/* Registered Address */}
                {registrationData.companyDetails.registeredAddress && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Registered Address</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-900">
                        {registrationData.companyDetails.registeredAddress.addressLine1}
                        {registrationData.companyDetails.registeredAddress.addressLine2 && (
                          <>, {registrationData.companyDetails.registeredAddress.addressLine2}</>
                        )}
                        <br />
                        {registrationData.companyDetails.registeredAddress.city}, {registrationData.companyDetails.registeredAddress.state} {registrationData.companyDetails.registeredAddress.pincode}
                        <br />
                        {registrationData.companyDetails.registeredAddress.country}
                      </p>
                    </div>
                  </div>
                )}

                {/* Manufacturing Address */}
                {registrationData.companyDetails.manufacturingAddress && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Manufacturing Address</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-900">
                        {registrationData.companyDetails.manufacturingAddress.addressLine1}
                        {registrationData.companyDetails.manufacturingAddress.addressLine2 && (
                          <>, {registrationData.companyDetails.manufacturingAddress.addressLine2}</>
                        )}
                        <br />
                        {registrationData.companyDetails.manufacturingAddress.city}, {registrationData.companyDetails.manufacturingAddress.state} {registrationData.companyDetails.manufacturingAddress.pincode}
                        <br />
                        {registrationData.companyDetails.manufacturingAddress.country}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tax Information */}
            {registrationData.taxInformation && (
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">GSTIN</label>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-sm text-gray-900">{registrationData.taxInformation.gstin || 'Not provided'}</p>
                      {registrationData.taxInformation.gstinValidationStatus && 
                        formatValidationStatus(registrationData.taxInformation.gstinValidationStatus)
                      }
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">PAN</label>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-sm text-gray-900">{registrationData.taxInformation.pan || 'Not provided'}</p>
                      {registrationData.taxInformation.panValidationStatus && 
                        formatValidationStatus(registrationData.taxInformation.panValidationStatus)
                      }
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CIN</label>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-sm text-gray-900">{registrationData.taxInformation.cin || 'Not provided'}</p>
                      {registrationData.taxInformation.cinValidationStatus && 
                        formatValidationStatus(registrationData.taxInformation.cinValidationStatus)
                      }
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Udyam Number</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.taxInformation.udyamNumber || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Authorized Signatory */}
            {registrationData.authorizedSignatory && (
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Authorized Signatory</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.authorizedSignatory.fullName || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Designation</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.authorizedSignatory.designation || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.authorizedSignatory.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.authorizedSignatory.mobileNumber || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Aadhaar (Masked)</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.authorizedSignatory.aadhaarMasked || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Passport Number</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.authorizedSignatory.passportNumber || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Banking Details */}
            {registrationData.bankingDetails && (
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Banking Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.bankingDetails.bankName || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Holder Name</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.bankingDetails.accountHolderName || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Number</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {registrationData.bankingDetails.accountNumber ? 
                        `****${registrationData.bankingDetails.accountNumber.slice(-4)}` : 
                        'Not provided'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.bankingDetails.ifscCode || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">GST Applicability</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.bankingDetails.gstApplicability || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Preferred Currency</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.bankingDetails.preferredCurrency || 'Not provided'}</p>
                  </div>
                </div>

                {/* Billing Address */}
                {registrationData.bankingDetails.billingAddress && (
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Billing Address</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-900">
                        {registrationData.bankingDetails.billingAddress.addressLine1}
                        {registrationData.bankingDetails.billingAddress.addressLine2 && (
                          <>, {registrationData.bankingDetails.billingAddress.addressLine2}</>
                        )}
                        <br />
                        {registrationData.bankingDetails.billingAddress.city}, {registrationData.bankingDetails.billingAddress.state} {registrationData.bankingDetails.billingAddress.pincode}
                        <br />
                        {registrationData.bankingDetails.billingAddress.country}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Brand Information */}
            {registrationData.brandInformation && (
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Brand Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Brand Names</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {registrationData.brandInformation.brandNames && registrationData.brandInformation.brandNames.length > 0 
                        ? registrationData.brandInformation.brandNames.join(', ')
                        : 'Not provided'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Trademark Status</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.brandInformation.trademarkStatus || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Trademark Number</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.brandInformation.trademarkNumber || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Industry</label>
                    <p className="mt-1 text-sm text-gray-900">{registrationData.brandInformation.industry || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Product Categories</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {registrationData.brandInformation.productCategories && registrationData.brandInformation.productCategories.length > 0 
                        ? registrationData.brandInformation.productCategories.join(', ')
                        : 'Not provided'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Production Volume</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {registrationData.brandInformation.monthlyProductionVolume 
                        ? `${registrationData.brandInformation.monthlyProductionVolume.toLocaleString()} units`
                        : 'Not provided'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Compliance Declarations */}
            {registrationData.declarations && (
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Declarations</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full mr-3 ${registrationData.declarations.declarationsAccepted ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm text-gray-900">Legal Declarations Accepted</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full mr-3 ${registrationData.declarations.termsAccepted ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm text-gray-900">Terms & Conditions Accepted</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full mr-3 ${registrationData.declarations.privacyPolicyAccepted ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm text-gray-900">Privacy Policy Accepted</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full mr-3 ${registrationData.declarations.dataProcessingConsent ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm text-gray-900">Data Processing Consent Given</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button 
                variant="danger" 
                onClick={onReject}
                className="bg-red-600 hover:bg-red-700"
              >
                Reject
              </Button>
              <Button onClick={onApprove}>
                Approve
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}